// @ts-nocheck
import React, {
	useMemo,
	useState,
} from 'react';
import {
	Alert,
	Checkbox,
	Dropdown,
	Grid,
	Menu,
	Popover,
	Space,
} from 'antd';
import { Moment } from 'moment';
import {
	AccountingItemApprovalStates,
	FixtureTypes,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { toMoment } from '@shared/utils/date';
import type { GetFixtureDetailsResponse } from '@api/features/fixtures/getFixtureDetails';
import type { TcFixtureProps } from '@api/models/tc-fixture';
import AddButton from '@client/components/AddButton';
import Table from '@client/components/Table/Table';
import getInvoicePresets from '@client/utils/getInvoicePresets';
import Card from '@client/components/Card/Card';
import { getHireInvoiceColumns } from '@client/screens/fleet/VoyageDetailsScreen/helpers/tableColumns';
import DatePicker, { DatePickerValue } from '@client/components/DatePicker';
import Button from '@client/components/Button';
import {
	submitHireInvoice,
	updateVoyage,
} from '@client/lib/api';
import showErrorNotification from '@client/utils/showErrorNotification';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import useLocalStorage from '@client/utils/hooks/useLocalStorage';
import { useVoyage } from '../VoyageProvider/VoyageProvider';
import HireInvoiceForm from './HireInvoiceForm';
import styles from './HireInvoicesTable.module.css';
import {
	onDeleteHireInvoice,
	onUnsubmitHireInvoice,
} from './helpers/invoiceFunctions';

const HireInvoicesTable = () => {
	const {
		voyageDetails,
		fixtureDetails,
		refreshDetails,
		voyageDetailsLoading,
	} = useVoyage();

	//
	const tcFixtureDetails = fixtureDetails as GetFixtureDetailsResponse<TcFixtureProps>;

	const [open, setOpen] = useState<boolean | Values<typeof getInvoicePresets>>(false);
	const [showDeliveryDateForm, setShowDeliveryDateForm] = useState<boolean>(false);
	const [deliveryDate, setDeliveryDate] = useState<DatePickerValue | null>(null);

	const [hideDeleted, setHideDeleted] = useLocalStorage('hideDeletedInvoices', true);

	const invoices = voyageDetails.hireInvoices;

	const {
		readyForCumulative,
	} = voyageDetails;

	const screens = Grid.useBreakpoint();

	const expandedRowKeys = useMemo(() => {
		return (invoices ?? []).reduce<Array<number>>((acc, h) => {
			if (h.state === AccountingItemApprovalStates.REJECTED) {
				acc.push(h.id);
			}

			return acc;
		}, []);
	}, [invoices]);

	const hireInvoiceColumns = getHireInvoiceColumns({
		onDelete: async (id) => {
			await onDeleteHireInvoice(id);
			await refreshDetails();
		},
		onSubmit: async (id) => {
			if (invoices != null) {
				const requiresApproval = invoices[0]?.requiresApproval;
				const successMsg = requiresApproval ? 'Submitted for approval' : 'Invoice approved';
				await submitHireInvoice(id);
				showSuccessNotification(successMsg);
				await refreshDetails();
			}
		},
		onUndo: async (id) => {
			await onUnsubmitHireInvoice(id);
			refreshDetails();
		},
		voyageDetails,
		fixtureDetails: tcFixtureDetails,
	});

	const hasDeliveryDate = voyageDetails.deliveryDate != null;

	const presets = getInvoicePresets(tcFixtureDetails);

	const saveDeliveryDate = async () => {
		if (deliveryDate != null) {
			try {
				await updateVoyage(voyageDetails.id,
					{ deliveryDate: toMoment(deliveryDate as Moment).utcOffset(0) });
				refreshDetails();
			} catch (e) {
				if (deliveryDate !== undefined) {
					showErrorNotification('Could not update contract', e as Error);
				}
			}
		}

		setShowDeliveryDateForm(false);
	};

	const addButton = (
		<AddButton
			disabledTooltip="Cannot generate hire invoices before delivery date is set"
			data-tour="createHireInvoice"
			loading={voyageDetailsLoading}
		>
			{(fixtureDetails.type === FixtureTypes.TC_IN ?
				'New Statement' :
				'New Invoice'
			)}
		</AddButton>
	);

	const getExtraContent = () => {
		const isCumulative = readyForCumulative && fixtureDetails.fixed;

		const filteredPresets = Object.entries(presets).filter(
			([_, preset]: any) => {
				// When cumulative: remove hire invoices
				if (isCumulative) {
					return !preset?.includeHire;
				}

				return true;
			},
		);

		if (!readyForCumulative || isCumulative) {
			return (
				<Dropdown
					trigger={['click']}
					overlay={
						hasDeliveryDate ? (
							<Menu>
								{isCumulative && (
									<Menu.Item onClick={() => setOpen(true)}>
										New Cumulative Statement
									</Menu.Item>
								)}

								{filteredPresets.map(([key, preset]: any) => (
									<Menu.Item
										key={key}
										onClick={() => setOpen(preset)}
									>
										{preset?.btnTitle}
									</Menu.Item>
								))}
							</Menu>
						) : (
							<></>
						)
					}
				>
					{!hasDeliveryDate ? (
						<Popover
							trigger={['click']}
							placement="bottomLeft"
							title={(
								<Space direction="vertical">
									Set Delivery Date
								</Space>
							)}
							open={showDeliveryDateForm}
							onOpenChange={(v) => setShowDeliveryDateForm(v)}
							content={(
								<Space direction="vertical">
									<Alert
										message="You must set a delivery date to raise an invoice"
										type="warning"
										showIcon
									/>
									<br />
									<div style={{ textAlign: 'center' }}>
										<Space>
											Delivery Date
											<DatePicker
												defaultPickerValue={voyageDetails.commencementDate || undefined}
												maxDate={voyageDetails.completionDate || undefined}
												time
												defaultUtcOffset={voyageDetails.commencementPortUtcOffset}
												showNow={false}
												onChange={(v) => setDeliveryDate(v)}
											/>
										</Space>
									</div>
									<br />
									<div style={{ textAlign: 'center' }}>
										<Button
											onClick={() => saveDeliveryDate()}
											type="primary"
											disabled={deliveryDate === null}
											disabledTooltip="Please select a date before saving"
										>
											Save
										</Button>
									</div>
									<br />
								</Space>
							)}
							destroyTooltipOnHide
						>
							{addButton}
						</Popover>
					) : (
						addButton
					)}
				</Dropdown>
			);
		}

		return null;
	};

	const isBlankOpen = open?.btnTitle != null && open.btnTitle === 'Blank Invoice';

	const hasDeletedInvoices = (invoices ?? []).some((i) => i.deletedAt != null);
	const visibleInvoices = hideDeleted ?
		(invoices ?? []).filter((i) => i.deletedAt == null) :
		(invoices ?? []);

	return (
		<>
			<Card
				slim
				title={(fixtureDetails.type === FixtureTypes.TC_IN ?
					'Hire Statements' :
					'Hire Invoices'
				)}
				extra={(
					<Space>
						{hasDeletedInvoices && (
							<Checkbox
								checked={hideDeleted}
								onChange={(e) => setHideDeleted(e.target.checked)}
							>
								Hide deleted
							</Checkbox>
						)}
						{getExtraContent()}
					</Space>
				)}
			>
				<Table
					data-tour="hireInvoicesTable"
					size="small"
					rowKey="id"
					pagination={false}
					// @ts-ignore TODO
					columns={hireInvoiceColumns}
					loading={voyageDetailsLoading}
					dataSource={visibleInvoices}
					useCards={screens.xs}
					rowClassName={(row) => row.deletedAt != null ? styles.deletedInvoiceRow : ''}
					expandable={{
						expandedRowRender: (row) => (row.stateNote != null && (
							<div>
								<strong>Reason:</strong>
								<p>{row.stateNote}</p>
							</div>
						)),
						rowExpandable: (row) => row.stateNote != null,
						defaultExpandedRowKeys: expandedRowKeys,
					}}
				/>
			</Card>
			<HireInvoiceForm
				open={open}
				isBlankOpen={isBlankOpen}
				cumulative={isBlankOpen ? false : readyForCumulative}
				voyageDetails={voyageDetails}
				fixtureDetails={tcFixtureDetails}
				onClose={() => setOpen(false)}
				onInvoiceCreated={refreshDetails}
				readOnly={false}
			/>
		</>
	);
};

export default HireInvoicesTable;
